import ajax from '../utils/request'
import ArticleService from './article.service'

/**
 * TopicService
 * @module service/Topic
 */
export default {
  /**
   * 获取全部频道列表
   * @emits  public/v1/topic/getList:getTopicList
   * @param {Number} [data.score]    分页标识
   * @param {Number} [data.size=12]  页面大小
   * @return  {Array<Object>}      返回的数据与接口一致
   */
  getTopicList (data) {
    const postData = {
      size: data.size | 0 || 12
    }

    if (data.score) {
      postData.score = data.score
    }

    /**
     * 执行获取全部频道列表的接口
     * @see http://zentao.geekbang.org/zentao/doc-view-485.html#wow2
     * @event public/v1/topic/getList:getTopicList
     * @property {Object}      data            request body
     * @property {Number}     [data.score]     分页标识
     * @property {Number}     [data.size=12]   每页条数
     * @returns  {Object}     res         返回的数据
     * @returns  {String}     res.id      频道 ID
     * @returns  {String}     res.name    频道名称
     * @returns  {Url}        res.cover   封面
     * @returns  {String}     res.desc    频道描述
     * @returns  {Timestamp}  res.ctime   创建时间
     * @returns  {String}     res.is_sub  是否已关注
     * @returns  {Number}     res.type    类型 [1:必选, 2:可选]
     * @returns  {Number}     res.score   分页标识
     */
    return ajax({
      url: 'public/v1/topic/getList',
      data: postData
    })
  },
  /**
   * 获取频道详情
   * @emits   public/v1/topic/getInfo:getTopicInfo
   * @param   {Number|String}  id  专题 ID 或者别名
   * @return  {Object}      返回的数据与接口一致
   */
  getTopicInfo (id) {
    const postData = {}

    if (/^\d+$/.test(id + '')) {
      postData.id = id - 0
    } else {
      postData.alias = (id + '').replace(/^\/+/i, '')
    }

    /**
     * 执行获取频道详情的接口
     * @see http://zentao.geekbang.org/zentao/doc-view-485.html#wow1
     * @event public/v1/topic/getInfo:getTopicInfo
     * @property {Object}    data           request body
     * @property {Number}   [data.id]       频道 ID
     * @property {String}   [data.alias]    频道别名
     * @returns  {Object}   res                 返回的数据
     * @returns  {String}   res.id              频道 ID
     * @returns  {String}   res.name            频道名称
     * @returns  {String}   res.cover           频道封面
     * @returns  {Date}     res.ctime           创建日期
     * @returns  {Boolean}  res.is_sub          是否已关注
     * @returns  {Array}    res.link_list       相关频道
     * @returns  {String}   res.link_list.id    频道 ID
     * @returns  {String}   res.link_list.name  频道名称
     */
    return ajax({
      url: 'public/v1/topic/getInfo',
      data: postData
    })
  },
  /**
   * 关注/取消关注频道
   * @emits  public/v1/subtopic/sub:followTopic
   * @param {Number}   id      频道 ID
   * @param {Boolean}  follow  是否关注 [true:关注, false:取消]
   */
  followTopic (id, follow) {
    const url = follow ? 'public/v1/subtopic/sub' : 'public/v1/subtopic/unsub'

    /**
     * 执行获取频道详情的接口
     * @event    public/v1/subtopic/sub:followTopic
     * @param   {Object}   data       body data
     * @param   {Number}   data.id    频道 ID
     */
    return ajax({
      signin: true,
      url,
      data: {
        id: id - 0
      }
    })
  },
  /**
   * 引导页 推荐频道列表
   */
  getRecommend () {
    return ajax({
      url: 'public/v1/topic/getRecommend'
    })
    /**
     * @param {Object}   res          返回数据
     * @param {String}   res.cover    封面
     * @param {Date}     res.ctime    创建时间
     * @param {String}   res.desc     描述
     * @param {Number}   res.id       ID
     * @param {Boolean}  res.is_sub   是否关注
     * @param {String}   res.name     频道名称
     * @param {Number}   res.type     是否必选  1：必选  2：可选
     * @param {Number}   res.score    分页标识
     */
    .then(res => {
      return res
    })
  },
  /**
   * 批量关注频道
   * @param {Array} data  关注的频道id  数组
   */
  batchSub (data) {
    return ajax({
      url: 'public/v1/subtopic/batchSub',
      data: {
        ids: data
      }
    })
  },
  /**
   * 获取 SEO 聚合页文章列表
   * @fires public/v1/misc/getListByTag:getSeoTagList
   * @param   {String}   name            tag 名称
   * @return  {Array<Object>}  res       数据基本与接口返回一致，追加了单独的作者或者译者的数据
   * @return  {Object}  res.authorData               单独的作者或者译者的数据
   * @return  {Number}  res.authorData.uid           作者或者译者的 ID
   * @return  {String}  res.authorData.nickname      作者或者译者的名称
   * @return  {String}  res.authorData.avatar        作者或者译者的头像地址
   * @return  {String}  res.authorData.authorLabel   作者或者译者的名词
   */
  getSeoTagList (name, page) {
    const postData = {
      name
    }

    /**
     * 获取 SEO 聚合页文章列表
     * @see https://yapi.geekbang.org/project/116/interface/api/6546
     * @event public/v1/misc/getListByTag:getSeoTagList
     * @param {Object}        data          data body
     * @param {String}        data.name     tag 名称
     * @return {Array<Object>}  res                     数据
     * @return {String}         res.id                  ID
     * @return {String}         res.aid                 文章 ID
     * @return {Number}         res.type                文章类型 [1:文章,8:电子书]
     * @return {Number}         res.ctype               是否是专题 [0:否,1:是]
     * @return {Number}         res.sub_type            是否是话题 [0:否,1:是]
     * @return {String}         res.views               阅读数
     * @return {String}         res.uuid                UUID
     * @return {String}         res.info                扩展数据
     * @return {String}         res.status              状态 [1:上架, 0:下架] 返回值只有1
     * @return {TimeStamp}      res.publish_time        发布时间
     * @return {TimeStamp}      res.ctime               创建时间
     * @return {TimeStamp}      res.utime               修改时间
     * @return {String}         res.article_title       标题
     * @return {String}         res.article_sharetitle  分享标题
     * @return {String}         res.article_subtitle    副标题
     * @return {String}         res.article_summary     摘要
     * @return {URL}            res.article_cover       封面
     * @return {String}         res.article_cover_point 图坐标组成
     * @return {Array<Object>}  res.author              作者
     * @return {String}         res.author.uid          作者 ID
     * @return {String}         res.author.nickname     作者名称
     * @return {URL}            res.author.avatar       译者头像
     * @return {Array<Object>}  res.translator          译者
     * @return {String}         res.translator.uid      译者 ID
     * @return {String}         res.translator.nickname 译者名称
     * @return {URL}            res.translator.avatar   译者头像
     * @return {Array<Object>}  res.topic               文章所属频道
     * @return {String}         res.topic.id            频道 ID
     * @return {String}         res.topic.name          频道标题
     * @return {Number}         res.love                点赞数
     */
    return ajax({
      url: 'public/v1/misc/getListByTag',
      data: postData
    }).then(res => {
      res.list = Array.isArray(res.list) ? res.list : []
      res.list = res.list.map(article => {
        article.article_rich_summary = article.article_summary

        return ArticleService.parseArticleAuthor(article)
      })

      return res
    })
  },
  /**
   * 获取 SEO 聚合页推荐标签
   * @fires public/v1/misc/getTagList:getSeoSidebarTag
   * @param   {Numner}   size     获取的数量
   * @return  {Object}  res       数据基本与接口返回一致
   */
  getSeoSidebarTag (size = 18) {
    const postData = {
      size
    }

    /**
     * 获取 SEO 聚合页推荐标签的接口
     * @see https://yapi.geekbang.org/project/116/interface/api/6752
     * @event public/v1/misc/getTagList:getSeoSidebarTag
     * @param   {Numner}   size     获取的数量
     * @return  {Object}   res      [key:标签ID,value:标签文案]
     */
    return ajax({
      url: 'public/v1/misc/getTagList',
      data: postData
    }).then(res => {
      const list = []

      Object.keys(res).forEach(key => {
        list.push({
          id: key,
          label: res[key]
        })
      })

      return list
    })
  },
  /**
   * 获取相关文章列表
   */
  getRelatedList (tagName) {
    const postData = {
      name: tagName
    }
    return ajax({
      url: 'public/v1/misc/getRelatedList',
      data: postData
    })
  }
}
